import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import StatCards from "../../components/dashboard/StatsCard";
import ActiveAlarm from "../../components/dashboard/AlarmBanner";
import FallAlarmsTable from "../../components/dashboard/FallTable";
import HeartbeatTable from "../../components/dashboard/HeartbeatTable";
import AlarmHistory from "../../components/dashboard/Alarmhistory";
import NewHeader from "../../components/Header/NewHeader";
import AppSidebar from "../../components/dashboard/Appsidebar";
//import TemperatureDashboard from "../../components/dashboard/TemperatureDashboard";
//import DoorLockDashboard from "../../components/dashboard/DoorLockDashboard";
import {
  getLatestAlarm,
  getDashboardSummary,
  getDevicesByTimeRange,
  getAllMsgDetails,
} from "../../services/dashboard.api";

// Builds a stable identity for an alarm so we can tell "same alarm the
// user already acknowledged" apart from "a brand-new alarm just fired".
function getAlarmKey(alarm) {
  if (!alarm) return null;
  return `${alarm.deviceID ?? ""}-${alarm.time ?? ""}`;
}

function Dashboard() {
  const [alarmData, setAlarmData] = useState({});
  const [summaryData, setSummaryData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAlarm, setShowAlarm] = useState(false);
  const [latestAlarmData, setLatestAlarmData] = useState(null);

  // Ref (not state) so the 2s polling interval — set up once on mount —
  // always reads the latest acknowledged alarm instead of a stale value
  // captured when the interval was created.
  const acknowledgedKeyRef = useRef(null);

  const handleAcknowledge = () => {
    console.log("Acknowledged");
    acknowledgedKeyRef.current = getAlarmKey(alarmData);
    setShowAlarm(false);
  };

  const handleSilence = () => console.log("Silenced");

  const fetchLatestAlarm = async () => {
    try {
      const latestAlarmResponse = await getLatestAlarm();
      setLatestAlarmData(latestAlarmResponse);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [alarmsResponse, dashboardSummaryResponse] = await Promise.all([
        getAllMsgDetails("Fall detector alarm", "5m"),
        getDashboardSummary(),
      ]);
      setSummaryData(dashboardSummaryResponse);

      if (alarmsResponse && alarmsResponse.length > 0) {
        const latest = alarmsResponse[0];
        setAlarmData(latest);

        // Only re-show the banner if this isn't the same alarm the user
        // already acknowledged. A genuinely new alarm (different device
        // or timestamp) still pops the banner back up.
        const key = getAlarmKey(latest);
        setShowAlarm(key !== acknowledgedKeyRef.current);
      } else {
        setShowAlarm(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchLatestAlarm();
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchLatestAlarm();
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f3f4f6",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER — spans full width, above the sidebar+content row */}
      <NewHeader />

      {/* SIDEBAR + BODY ROW */}
      <Box sx={{ display: "flex", flex: 1 }}>
        <AppSidebar />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 1.5, sm: 2, md: 2.5 },
            pt: { xs: 1.5, sm: 2, md: 2.5 },
            pb: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, md: 2 },
          }}
        >
          {/* ACTIVE ALARM BANNER */}
          {showAlarm && alarmData && (
            <Box sx={{ mt: 2, mb: -1 }}>
              <ActiveAlarm
                alarm={{
                  timestamp: alarmData?.time,
                  deviceId: alarmData?.deviceID,
                  type: alarmData?.msgType,
                  payload: alarmData?.data,
                }}
                onAcknowledge={handleAcknowledge}
                onSilence={handleSilence}
              />
            </Box>
          )}

          {/* STAT CARDS */}
          <StatCards
            totalDevices={summaryData?.totalDevices || 0}
            offlineCount={summaryData?.inactiveDevices || 0}
            fallAlarms={summaryData?.fallAlarmCount24h || 0}
            lastAlarmTime={latestAlarmData?.time || null}
            heartbeats={summaryData?.heartbeatDeviceCount1h || 0}
          />

          {/* ALARM HISTORY */}
          <AlarmHistory latestAlarmTime={alarmData?.time} />
          {/* <TemperatureDashboard /> */}
          {/* <DoorLockDashboard /> */}
        </Box>
      </Box>
    </Box>
  );
}

export default Dashboard;
